import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CmsProvider } from "@/context/CmsContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProjectFilterProvider } from "@/context/ProjectFilterContext";
import { ProtectedRoute, AdminRoute, SuperAdminRoute, ConfigRoute, NonExecRoute, FullAccessRoute, NotManagerRoute, TargetsRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ExecutiveOverview from "@/pages/ExecutiveOverview";
import VerticalLeadDashboard from "@/pages/VerticalLeadDashboard";
import AnalyticsHub from "@/pages/AnalyticsHub";
import ProjectDetail from "@/pages/ProjectDetail";
import SpendsLog from "@/pages/SpendsLog";
import BudgetAllocation from "@/pages/BudgetAllocation";
import VendorScorecards from "@/pages/VendorScorecards";
import ReferralTracker from "@/pages/ReferralTracker";
import TargetsModule from "@/pages/TargetsModule";
import Invoices from "@/pages/Invoices";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import AdminCms from "@/pages/AdminCms";
import MarketingPlan from "@/pages/MarketingPlan";
import DataIntegrity from "@/pages/DataIntegrity";

function ShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

// Vertical leads (managers) land on their lightweight dashboard ONLY if an
// admin has granted access; otherwise they're sent to Invoices. Everyone else
// with dashboard access sees the full Executive Overview.
function DashboardRouter() {
  const { isManager, dashboardAccess } = useAuth();
  if (isManager && !dashboardAccess) return <Navigate to="/invoices" replace />;
  return isManager ? <VerticalLeadDashboard /> : <ExecutiveOverview />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <CmsProvider>
          <CurrencyProvider>
            <ProjectFilterProvider>
              <TooltipProvider delayDuration={150}>
                <BrowserRouter>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route element={<ProtectedRoute><ShellLayout /></ProtectedRoute>}>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<NonExecRoute><DashboardRouter /></NonExecRoute>} />
                      <Route path="/marketing-plan" element={<ConfigRoute><MarketingPlan /></ConfigRoute>} />
                      <Route path="/marketing-strategy" element={<Navigate to="/marketing-plan?tab=strategy" replace />} />
                      <Route path="/analytics" element={<FullAccessRoute><AnalyticsHub /></FullAccessRoute>} />
                      <Route path="/taxonomy" element={<Navigate to="/analytics?dim=project" replace />} />
                      <Route path="/monthly" element={<Navigate to="/analytics?dim=month" replace />} />
                      <Route path="/projects" element={<Navigate to="/analytics?dim=project" replace />} />
                      <Route path="/projects/:name" element={<FullAccessRoute><ProjectDetail /></FullAccessRoute>} />
                      <Route path="/spends" element={<SpendsLog />} />
                      <Route path="/invoices" element={<Invoices />} />
                      <Route path="/vendors" element={<NotManagerRoute><VendorScorecards /></NotManagerRoute>} />
                      <Route path="/referrals" element={<FullAccessRoute><ReferralTracker /></FullAccessRoute>} />
                      <Route path="/verticals" element={<Navigate to="/analytics?dim=vertical" replace />} />
                      <Route path="/channels" element={<Navigate to="/analytics?dim=vendor" replace />} />
                      <Route path="/budget-allocation" element={<FullAccessRoute><BudgetAllocation /></FullAccessRoute>} />
                      <Route path="/budget-plan" element={<Navigate to="/budget-allocation" replace />} />
                      <Route path="/budgets" element={<Navigate to="/budget-allocation" replace />} />
                      <Route path="/targets" element={<TargetsRoute><TargetsModule /></TargetsRoute>} />
                      <Route path="/targets-grid" element={<Navigate to="/targets" replace />} />
                      <Route path="/governance/audit-log" element={<AdminRoute><AuditLog /></AdminRoute>} />
                      <Route path="/governance/data-integrity" element={<SuperAdminRoute><DataIntegrity /></SuperAdminRoute>} />
                      <Route path="/audit" element={<Navigate to="/governance/audit-log" replace />} />
                      <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
                      <Route path="/admin/cms" element={<SuperAdminRoute><AdminCms /></SuperAdminRoute>} />
                    </Route>
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                  <Toaster position="top-right" />
                </BrowserRouter>
              </TooltipProvider>
            </ProjectFilterProvider>
          </CurrencyProvider>
          </CmsProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
